import {DestroyRef, Injectable, computed, inject, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {EMPTY, Observable, Subject, Subscription} from 'rxjs';
import {catchError, expand, map, reduce, switchMap} from 'rxjs/operators';
import {HttpErrorResponse} from '@angular/common/http';
import * as moment from 'moment';
import * as _ from 'lodash';
import {TimekeepingHttpService} from '../services/timekeeping-http.service';
import {TimekeepingAlertsService} from '../services/timekeeping-alerts.service';
import {
  AgentClockEvent,
  AgentClockEventType,
  TimekeepingClockEventsService
} from '../services/timekeeping-clock-events.service';
import {AgentsHttpService} from '../../../agents/angular/services/agents-http.service';
import {injectLegacy} from '../../../commons/angular/utils/inject-legacy.util';
import type {SplitService} from '../../../commons/services/split-service.service';
import type {
  AgentConfigurationProfilesRequestsService
} from '../../../configuration-profiles/services/agent-configuration-profiles-requests.service';
import {TimeCardGroup} from '../models/timecard-group.model';
import {TimeCardModel} from '../models/timecard.model';
import {TimeCardAnalysis} from '../models/timecard-analysis.model';
import {TimeCardHistoryItem} from '../models/timecard-history.model';
import {TimeCardStatusType} from '../../types/timecard-status.type';
import {TimeCardDataSourceType} from '../../types/timecard-data-source.type';
import {TimeCardEntryType} from '../../types/timecard-entry-type.type';
import {Stop} from '../../types/timecard-detail-view.type';
import {AddStopDialogResult, EditAdminTimeChange} from '../../types/timekeeping-dialog.type';
import {Address} from '../../../addresses/models/address.model';
import {
  TimecardAgentPerformance,
  TimecardDetailRouteParams,
  TimecardEditableField,
  TimecardLocationPoint,
  TimecardShellContext,
  TimecardShift
} from '../models/timecard-detail.model';
import {
  DEFAULT_GENERALIZED_LOCATION_RADIUS_METERS,
  EMPTY_AGENT_PERFORMANCE,
  EMPTY_AGGREGATED_DATA,
  applyEntryFieldUpdate,
  buildActualTrackingStops,
  buildAddBreakPayload,
  buildAddCustomTaskPayload,
  buildAddLocationPayload,
  buildAddTaskPayload,
  buildAggregatedData,
  buildEntryAnalysisMap,
  buildGeneralizedLocationCircles,
  buildLocationUpdatesByHour,
  buildMapTasks,
  buildUpdateStopTimesPayload,
  buildWorkingSlots,
  canEditAdminTime,
  cloneTimecardForUpdate,
  findClosestLocation,
  getLatestLocation,
  mapLeaves,
  mapShifts
} from '../utils/timecard-detail.mapper';

interface TimecardDetailState {
  route: TimecardDetailRouteParams;
  fromDate: number;
  toDate: number;
  group: TimeCardGroup | null;
  analysisMap: Record<string, TimeCardAnalysis>;
  statusHistoryShiftIds: string[];
  blockApprovalOnInfeasible: boolean;
  selectedShiftId?: string;
  selectedTaskId?: string;
  /** Route data on the map and timeline. Kept while a reload is in flight, so they don't blink. */
  performance?: TimecardAgentPerformance;
  performanceByShift: Record<string, TimecardAgentPerformance>;
  agentLocationCoords?: TimecardLocationPoint;
  isLoadingData: boolean;
  isStatusChanging: boolean;
  isRefreshingData: boolean;
  isUpdatingEntry: boolean;
  isMapTimelineVisible: boolean;
  isRoutePanelVisible: boolean;
  isMapFullscreen: boolean;
  isShowLegend: boolean;
}

type TimecardDetailStateUpdate = Partial<TimecardDetailState> | ((state: TimecardDetailState) => Partial<TimecardDetailState>);

const HISTORY_PAGE_SIZE = 100;

const initialState: TimecardDetailState = {
  route: {agentId: undefined},
  fromDate: undefined,
  toDate: undefined,
  group: null,
  analysisMap: {},
  statusHistoryShiftIds: [],
  blockApprovalOnInfeasible: true,
  selectedShiftId: undefined,
  selectedTaskId: undefined,
  performance: EMPTY_AGENT_PERFORMANCE,
  performanceByShift: {},
  agentLocationCoords: undefined,
  isLoadingData: false,
  isStatusChanging: false,
  isRefreshingData: false,
  isUpdatingEntry: false,
  isMapTimelineVisible: true,
  isRoutePanelVisible: false,
  isMapFullscreen: false,
  isShowLegend: true
};

/**
 * State of the timecard detail screen (one agent, one day). Provided by the screen container.
 */
@Injectable()
export class TimecardDetailStore {

  private readonly timekeepingHttpService = inject(TimekeepingHttpService);
  private readonly agentsHttpService = inject(AgentsHttpService);
  private readonly alerts = inject(TimekeepingAlertsService);
  private readonly clockEventsService = inject(TimekeepingClockEventsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly splitService = injectLegacy<SplitService>('splitService');
  private readonly agentConfigurationProfilesRequestsService =
    injectLegacy<AgentConfigurationProfilesRequestsService>('agentConfigurationProfilesRequestsService');

  private readonly state = signal<TimecardDetailState>(initialState);

  // Selectors
  readonly route = computed(() => this.state().route);
  readonly fromDate = computed(() => this.state().fromDate);
  readonly group = computed(() => this.state().group);
  readonly analysisMap = computed(() => this.state().analysisMap);
  readonly selectedShiftId = computed(() => this.state().selectedShiftId);
  readonly selectedTaskId = computed(() => this.state().selectedTaskId);
  readonly isLoadingData = computed(() => this.state().isLoadingData);
  readonly isStatusChanging = computed(() => this.state().isStatusChanging);
  readonly isRefreshingData = computed(() => this.state().isRefreshingData);
  readonly isUpdatingEntry = computed(() => this.state().isUpdatingEntry);
  readonly isMapTimelineVisible = computed(() => this.state().isMapTimelineVisible);
  readonly isRoutePanelVisible = computed(() => this.state().isRoutePanelVisible);
  readonly isMapFullscreen = computed(() => this.state().isMapFullscreen);
  readonly isShowLegend = computed(() => this.state().isShowLegend);
  readonly blockApprovalOnInfeasible = computed(() => this.state().blockApprovalOnInfeasible);

  readonly timecards = computed<TimeCardModel[]>(() => this.group()?.timeCards || []);
  readonly shifts = computed(() => mapShifts(this.timecards(), this.state().blockApprovalOnInfeasible, this.splitService.getBreakTypes()));
  readonly leaves = computed(() => mapLeaves(this.timecards()));
  readonly statusHistoryShiftIds = computed(() => new Set(this.state().statusHistoryShiftIds));
  readonly entryAnalysisMap = computed(() => buildEntryAnalysisMap(this.analysisMap()));
  readonly aggregatedData = computed(() => this.group() ? buildAggregatedData(this.group(), this.shifts()) : EMPTY_AGGREGATED_DATA);
  readonly employee = computed(() => {
    const group = this.group();
    return {
      name: group?.agentReference?.fullName || '',
      employeeId: group?.agentReference?.externalId || '-',
      tags: group?.agentReference?.tags || []
    };
  });

  readonly editableShift = computed<TimecardShift | null>(() => _.find(this.shifts(), 'isEditable') || null);
  readonly isStopButtonDisabled = computed(() => !this.editableShift() || this.isUpdatingEntry());
  readonly canEditAdminTime = computed(() => !!this.group() && !this.isUpdatingEntry() && canEditAdminTime(this.shifts()));

  private readonly generalizedRadiusMeters = computed(() =>
    this.splitService.getSiteInfo()?.geofencingArrivalTaskRadius || DEFAULT_GENERALIZED_LOCATION_RADIUS_METERS);
  private readonly selectedTimecard = computed(() => _.find(this.timecards(), {id: this.selectedShiftId()}));
  readonly selectedAgentTasks = computed(() =>
    this.selectedTimecard() ? buildMapTasks(this.selectedTimecard(), this.generalizedRadiusMeters()) : []);
  private readonly selectedPerformance = computed(() => this.state().performance);
  private readonly locationUpdatesByHour = computed(() => buildLocationUpdatesByHour(this.selectedPerformance()));

  /** What the AngularJS map, timeline and agent select need. */
  readonly shellContext = computed<TimecardShellContext>(() => {
    const group = this.group();
    const timecards = this.timecards();
    const performance = this.selectedPerformance();
    const agentId = group?.agentId || this.route().agentId;
    return {
      selectedAgent: {
        id: agentId || '',
        name: group ? (group.agentReference?.fullName || '') : 'Loading...',
        skills: group?.agentReference?.tags || []
      },
      selectedAgentTasks: this.selectedAgentTasks(),
      selectedTaskId: this.selectedTaskId(),
      agentWorkingSlots: buildWorkingSlots(timecards),
      selectedAgentPerformance: performance,
      agentLocationCoords: this.state().agentLocationCoords,
      agentIrregularities: performance?.agentIrregularity ? [{...performance.agentIrregularity, agentId}] : [],
      actualTrackingStops: buildActualTrackingStops(timecards),
      generalizedLocationCircles: this.selectedTimecard()
        ? buildGeneralizedLocationCircles(this.selectedTimecard(), this.generalizedRadiusMeters())
        : [],
      isMapTimelineVisible: this.isMapTimelineVisible(),
      isRoutePanelVisible: this.isRoutePanelVisible(),
      fromDate: this.fromDate(),
      toDate: this.state().toDate
    };
  });

  /** Emits `keepSelection`: true for a background refresh that keeps the selected shift. */
  private readonly loadGroup$ = new Subject<boolean>();
  private analysisSubscription: Subscription;
  private statusHistorySubscription: Subscription;
  private updateSubscription: Subscription;
  private clockEventsSubscription: Subscription;

  constructor() {
    // switchMap: a newer day cancels the request still in flight, so a late response never shows the wrong day.
    this.loadGroup$.pipe(
      switchMap((keepSelection) => this.timekeepingHttpService.getTimeCardGroups({
        agentIds: [this.state().route.agentId],
        from: this.state().fromDate,
        to: this.state().toDate,
        returnTimeCards: true,
        limit: 1
      }).pipe(
        map((response) => response?.list?.[0] || null),
        catchError(() => [null]),
        map((group) => ({group, keepSelection}))
      )),
      takeUntilDestroyed()
    ).subscribe(({group, keepSelection}) => this.applyGroup(group, keepSelection));
  }

  // ******************************
  //        Init & loading
  // ******************************

  init(route: TimecardDetailRouteParams) {
    this.patch({
      route,
      fromDate: route.fromDate || moment().startOf('day').valueOf(),
      toDate: route.toDate || moment().endOf('day').valueOf()
    });

    if (!route.agentId) {
      return;
    }

    this.agentConfigurationProfilesRequestsService.getConfigurationByAgent(route.agentId)
      .then((profile: { shiftRestrictions?: { agentTimekeepingPreferences?: { blockApprovalOnInfeasibleTimeCards?: boolean } } }) => {
        const block = profile?.shiftRestrictions?.agentTimekeepingPreferences?.blockApprovalOnInfeasibleTimeCards;
        if (block !== undefined) {
          this.patch({blockApprovalOnInfeasible: block});
        }
      })
      .catch((error: unknown) => console.error('Failed to fetch agent profile:', error));

    this.clockEventsSubscription?.unsubscribe();
    this.clockEventsSubscription = this.clockEventsService.watch([route.agentId])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => this.onClockEvent(event));
    this.reload();
  }

  reload() {
    if (!this.state().route.agentId) {
      return;
    }
    this.patch({isLoadingData: true});
    this.loadGroup$.next(false);
  }

  setDate(date: number) {
    this.patch({
      fromDate: moment(date).startOf('day').valueOf(),
      toDate: moment(date).endOf('day').valueOf(),
      performance: undefined,
      performanceByShift: {}
    });
    this.reload();
  }

  browseDay(isNext: boolean) {
    this.setDate(moment(this.state().fromDate).add(isNext ? 1 : -1, 'day').valueOf());
  }

  private applyGroup(group: TimeCardGroup | null, keepSelection = false) {
    // A failed background refresh keeps the day on screen
    if (keepSelection && !group) {
      return;
    }

    const shifts = group
      ? mapShifts(group.timeCards || [], this.state().blockApprovalOnInfeasible, this.splitService.getBreakTypes())
      : [];
    const selectedShift = (keepSelection && _.find(shifts, {id: this.state().selectedShiftId})) || shifts[0];
    // A background refresh keeps the current agent location
    const firstTimecard = keepSelection ? undefined : _.find(group?.timeCards, {id: selectedShift?.id});
    const firstTask = firstTimecard ? buildMapTasks(firstTimecard, this.generalizedRadiusMeters())[0] : undefined;
    const performanceByShift = keepSelection ? this.unchangedShiftsPerformance(shifts) : {};

    this.patch((state) => ({
      group,
      isLoadingData: false,
      // An empty day shows an empty route; otherwise the current route stays until the new one arrives
      performance: group ? state.performance : EMPTY_AGENT_PERFORMANCE,
      performanceByShift,
      selectedShiftId: selectedShift?.id,
      // A task of a shift that is no longer selected doesn't belong to the new one
      selectedTaskId: keepSelection && selectedShift?.id !== state.selectedShiftId ? undefined : state.selectedTaskId,
      // Agent location falls back to the first task until the route data arrives
      agentLocationCoords: firstTask
        ? {lat: firstTask.taskSpec.address.location.lat, lng: firstTask.taskSpec.address.location.lng}
        : state.agentLocationCoords
    }));

    if (!group) {
      return;
    }
    this.loadStatusHistoryFlags(_.map(shifts, 'id'));
    this.loadAnalysis(_.map(group.timeCards, 'id'));
    if (selectedShift) {
      this.loadPerformance(selectedShift);
    }
  }

  // ******************************
  //        Live updates
  // ******************************

  /** Reloads the day in the background when the clock event changes what is on screen. */
  private onClockEvent(event: AgentClockEvent) {
    // A load in flight is for a day the shown timecards no longer describe; it brings the fresh ones anyway
    if (this.state().isLoadingData) {
      return;
    }
    const isShownDayChanged = event.type === AgentClockEventType.ClockIn
      ? this.isOpenedTimecardMissing(event.timeCardId)
      : this.isClosedTimecardShownOpen(event.timeCardId);
    if (isShownDayChanged) {
      this.loadGroup$.next(true);
    }
  }

  /** A timecard opened now belongs to the shown day only if the day is today. */
  private isOpenedTimecardMissing(timeCardId: string) {
    const now = Date.now();
    return now >= this.state().fromDate && now <= this.state().toDate && !this.findTimecard(timeCardId);
  }

  private isClosedTimecardShownOpen(timeCardId: string | null) {
    return _.some(this.timecards(), (timecard) =>
      timecard.status === TimeCardStatusType.Open && (!timeCardId || timecard.id === timeCardId));
  }

  /** Route data stays cached for shifts whose clock times didn't change. */
  private unchangedShiftsPerformance(shifts: TimecardShift[]): Record<string, TimecardAgentPerformance> {
    const previousShifts = _.keyBy(this.shifts(), 'id');
    return _.pickBy(this.state().performanceByShift, (performance, shiftId) => {
      const shift = _.find(shifts, {id: shiftId});
      return !!shift
        && shift.clockInTime === previousShifts[shiftId]?.clockInTime
        && shift.clockOutTime === previousShifts[shiftId]?.clockOutTime;
    });
  }

  private loadAnalysis(timeCardIds: string[]) {
    // Drop a pending request so a late response for the previous timecards can't overwrite the fresh analysis
    this.analysisSubscription?.unsubscribe();
    if (!timeCardIds.length) {
      return;
    }
    this.analysisSubscription = this.timekeepingHttpService.getTimeCardAnalysis(timeCardIds)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (analysisList) => this.patch({analysisMap: _.keyBy(analysisList || [], 'timeCardId')}),
        // Analysis is non-blocking
        error: (error) => console.error('Failed to fetch timecard analysis:', error)
      });
  }

  private loadStatusHistoryFlags(shiftIds: string[]) {
    this.statusHistorySubscription?.unsubscribe();
    if (!shiftIds.length) {
      this.patch({statusHistoryShiftIds: []});
      return;
    }
    this.statusHistorySubscription = this.timekeepingHttpService.getTimeCardHistory(shiftIds, 0, shiftIds.length)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const historyByTimeCardId = _.keyBy(response?.content || [], 'timeCardId');
          this.patch({
            statusHistoryShiftIds: _.filter(shiftIds, (id) => !_.isEmpty(historyByTimeCardId[id]?.status?.changes))
          });
        },
        error: (error) => console.error('Failed to load timecard status history:', error)
      });
  }

  /** Route data of a shift, cached per shift. Applied only while that shift is still selected. */
  private loadPerformance(shift: TimecardShift) {
    if (!shift?.clockInTime || !shift?.clockOutTime) {
      return;
    }
    const cached = this.state().performanceByShift[shift.id];
    if (cached) {
      this.applyPerformance(cached);
      return;
    }
    this.agentsHttpService.getAgentPerformance(this.state().route.agentId, shift.clockInTime, shift.clockOutTime)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (performance) => {
          this.patch((state) => ({performanceByShift: {...state.performanceByShift, [shift.id]: performance}}));
          if (this.state().selectedShiftId === shift.id) {
            this.applyPerformance(performance);
          }
        },
        error: (error) => console.error('Failed to load agent performance:', error)
      });
  }

  private applyPerformance(performance: TimecardAgentPerformance) {
    const latest = getLatestLocation(performance);
    this.patch((state) => ({performance, agentLocationCoords: latest || state.agentLocationCoords}));
  }

  /** Agent's registered address, used by the Add Stop dialog for start/end locations. */
  loadAgentAddress(): Observable<Address | null> {
    const agentId = this.state().group?.agentId || this.state().route.agentId;
    return this.agentsHttpService.getOne(agentId).pipe(
      map((agent) => (agent as { address?: Address })?.address || null),
      catchError(() => [null])
    );
  }

  /** Every history page of a timecard, merged. */
  loadFullHistory(timeCardId: string): Observable<TimeCardHistoryItem[]> {
    return this.timekeepingHttpService.getTimeCardHistory(timeCardId, 0, HISTORY_PAGE_SIZE).pipe(
      map((response) => ({response, page: 0})),
      expand(({response, page}) => response.last || !response.content?.length
        ? EMPTY
        : this.timekeepingHttpService.getTimeCardHistory(timeCardId, page + 1, HISTORY_PAGE_SIZE)
          .pipe(map((next) => ({response: next, page: page + 1})))),
      reduce((items, {response}) => items.concat(response.content || []), [] as TimeCardHistoryItem[])
    );
  }

  // ******************************
  //        Selection & UI
  // ******************************

  selectShift(shiftId: string) {
    if (!shiftId || this.state().selectedShiftId === shiftId) {
      return;
    }
    // A task selected in the previous shift doesn't belong to the new one (a bundle task shares its id across shifts)
    this.patch({selectedShiftId: shiftId, selectedTaskId: undefined});
    this.loadPerformance(_.find(this.shifts(), {id: shiftId}));
  }

  toggleTask(taskId: string) {
    this.patch((state) => ({selectedTaskId: state.selectedTaskId === taskId ? undefined : taskId}));
  }

  selectStop(stop: Stop, shiftId: string) {
    this.selectShift(shiftId);
    const tasks = this.selectedAgentTasks();
    const task = _.find(tasks, {id: stop.id}) || _.find(tasks, (item) =>
      item.taskSpec.address.location.lat === stop.location.lat && item.taskSpec.address.location.lng === stop.location.lng);
    if (task) {
      this.toggleTask(task.id);
    }
  }

  setTimelineTimestamp(timestamp: number) {
    const closest = findClosestLocation(this.locationUpdatesByHour(), timestamp);
    if (closest) {
      this.patch({agentLocationCoords: closest});
    }
  }

  toggleMapTimeline() {
    this.patch((state) => ({isMapTimelineVisible: !state.isMapTimelineVisible}));
  }

  toggleRoutePanel() {
    this.patch((state) => ({isRoutePanelVisible: !state.isRoutePanelVisible}));
  }

  toggleMapFullscreen() {
    this.patch((state) => ({isMapFullscreen: !state.isMapFullscreen}));
  }

  setShowLegend(isShowLegend: boolean) {
    this.patch({isShowLegend});
  }

  // ******************************
  //        Mutations
  // ******************************

  changeStatus(shiftId: string, status: TimeCardStatusType) {
    this.patch({isStatusChanging: true});
    this.timekeepingHttpService.updateStatus(shiftId, status)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.patch({isStatusChanging: false});
          this.reload();
        },
        error: (error: HttpErrorResponse) => {
          console.error('Failed to update timecard status:', error);
          this.patch({isStatusChanging: false});
          this.alerts.updateError(error?.error?.more_info);
        }
      });
  }

  refreshData(shiftId: string) {
    if (this.state().isRefreshingData) {
      return;
    }
    this.patch({isRefreshingData: true});
    this.timekeepingHttpService.refreshTimeCardData(shiftId, TimeCardDataSourceType.Reported, true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.patch({isRefreshingData: false});
          this.reload();
          this.alerts.updateSuccess();
        },
        error: (error: HttpErrorResponse) => {
          this.patch({isRefreshingData: false});
          // On 400 (e.g. the timecard state changed) the view is silently reloaded
          if (error?.status === 400) {
            this.reload();
          } else {
            this.alerts.updateError(error?.error?.more_info);
          }
        }
      });
  }

  updateEntry(stop: Stop, field: TimecardEditableField, value: unknown) {
    const timecard = this.findTimecard(stop.timecardId);
    if (!timecard || stop.entryIndex === undefined) {
      return;
    }
    const payload = cloneTimecardForUpdate(timecard);
    const entry = payload.reportedData?.entries?.[stop.entryIndex];
    if (!entry) {
      console.error('Entry not found in reportedData');
      return;
    }
    applyEntryFieldUpdate(entry, field, value);
    this.saveTimecard(stop.timecardId, payload, `Failed to update ${field}:`);
  }

  updateAdminTime(changes: EditAdminTimeChange[]) {
    if (!this.state().group || !changes?.length) {
      return;
    }
    this.runUpdate(this.timekeepingHttpService.updateTimeCardsAdminTime(changes), 'Failed to update admin time:');
  }

  /** Applies the Add Stop dialog result: a new task, custom task, break or start/end location, or new times for an existing task. */
  addStop(result: AddStopDialogResult) {
    const timecard = this.findTimecard(result.timecardId);
    if (!timecard) {
      return;
    }

    if (result.stopType === TimeCardEntryType.Task) {
      if (result.isUpdateExisting && result.existingEntryIndex !== undefined) {
        const payload = buildUpdateStopTimesPayload(timecard, result.existingEntryIndex, result.arrival, result.departure);
        if (!payload) {
          console.error('Entry not found in reportedData');
          return;
        }
        this.saveTimecard(timecard.id, payload, 'Failed to update existing stop times:');
      } else {
        this.saveTimecard(timecard.id, buildAddTaskPayload(timecard, result), 'Failed to add new stop:');
      }
    } else if (result.stopType === TimeCardEntryType.CustomTask) {
      this.saveTimecard(timecard.id, buildAddCustomTaskPayload(timecard, result), 'Failed to add custom task:');
    } else if (result.stopType === TimeCardEntryType.Break) {
      if (!result.arrival || !result.departure || !result.breakTypeId) {
        return;
      }
      this.saveTimecard(timecard.id, buildAddBreakPayload(timecard, result), 'Failed to add new break:');
    } else {
      this.saveTimecard(timecard.id, buildAddLocationPayload(timecard, result), 'Failed to add location entry:');
    }
  }

  archiveStop(stop: Stop) {
    const timecard = this.findTimecard(stop.timecardId);
    if (!timecard || stop.entryIndex === undefined) {
      return;
    }
    const payload = cloneTimecardForUpdate(timecard);
    const entry = payload.reportedData?.entries?.[stop.entryIndex];
    if (!entry) {
      return;
    }
    entry.archive = true;
    this.saveTimecard(stop.timecardId, payload, 'Failed to archive stop:');
  }

  findTimecard(timeCardId: string): TimeCardModel | undefined {
    return _.find(this.timecards(), {id: timeCardId});
  }

  private saveTimecard(timeCardId: string, payload: Partial<TimeCardModel>, errorLog: string) {
    this.runUpdate(this.timekeepingHttpService.updateTimeCard(timeCardId, payload), errorLog);
  }

  /** One update at a time: a new one cancels the request still in flight. Every update reloads the day. */
  private runUpdate(request$: Observable<unknown>, errorLog: string) {
    this.updateSubscription?.unsubscribe();
    this.patch({isUpdatingEntry: true});
    this.updateSubscription = request$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.patch({isUpdatingEntry: false});
          this.reload();
          this.alerts.updateSuccess();
        },
        error: (error: HttpErrorResponse) => {
          console.error(errorLog, error);
          this.patch({isUpdatingEntry: false});
          this.alerts.updateError(error?.error?.more_info);
        }
      });
  }

  private patch(update: TimecardDetailStateUpdate) {
    this.state.update((state) => ({...state, ...(_.isFunction(update) ? update(state) : update)}));
  }
}
